// Maps (assetKind, angle) → URL of the PNG to display in the avatar stack.
// Two filename conventions:
//   - 'bucket'  : <prefix>angle_<lo>-<hi>.png  + special _0 / _minus5
//   - 'integer' : HeelSlideROM-<N>.png         (HSROM only)
//
// Assets live on the API server under /content/exercises/<Folder>/ (see
// physio_server/server.js Express.static mount). Both the patient app and
// the admin reference the same on-disk files via absolute URLs derived
// from the server base. Default falls back to http://localhost:5000 for
// local dev when the env var isn't set.
const SERVER_BASE =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) ||
  'http://localhost:5000'


// `dial` is the RomBandDial pivot + radius (in the dial's viewBox 0 0 230
// 240 coordinate system) tuned per asset kind so the band apex lands on
// the PNG body's anatomical knee after the `.isf-stack` / `.isf-dial`
// transforms in ImageSwapFigure. Values were derived by measuring the
// knee pivot pixel in a representative frame of each PNG set and back-
// solving for the dial coords that align post-`scale(1.0)`. See
// dialAnchorFor() below for the lookup helper used by ImageSwapFigure.
const KIND = {
  SITTING_LIFT:   { folder: 'SittingLift',  prefix: 'SittingLift_',  convention: 'bucket',  step: 5, min: -5,  max: 150, dial: { kneeX:  91, kneeY: 134, bandR: 104, viewBoxX: -15, viewBoxY: 30, viewBoxW: 230, viewBoxH: 240, imageOffsetX: -5, imageOffsetY: -10 } },
  // Sitting Lift 2 — same seated exercise, fresh PNG set in
  // content/exercises/SittingLift2/ (same SittingLift_angle_*.png filenames, so
  // prefix stays 'SittingLift_'). Dial geometry mirrors Sitting Lift so the
  // admin Configure-modal preview aligns; the patient app carries the extra
  // figure zoom/offset tuning for the live session.
  SITTING_LIFT_2: { folder: 'SittingLift2', prefix: 'SittingLift_',  convention: 'bucket',  step: 5, min: -5,  max: 150, dial: { kneeX:  91, kneeY: 134, bandR: 104, viewBoxX: -15, viewBoxY: 30, viewBoxW: 230, viewBoxH: 240, imageOffsetX: -5, imageOffsetY: -10 } },
  // Sit-to-Stand floor is 0° (no `SitToStand_angle_minus5.png` shipped) —
  // negative live angles clamp to 0 and render the seated 0° PNG.
  // Ceiling capped at 90° (= restAngle / fully seated pose). Live angles
  // beyond 90° clamp to 90 and keep the seated PNG, because bending past
  // the chair pose makes no physical sense for this exercise even though
  // the higher buckets (91-110) exist on disk.
  SIT_TO_STAND:   { folder: 'SitToStand',   prefix: 'SitToStand_',   convention: 'bucket',  step: 5, min:  0,  max:  90, dial: { kneeX:  95, kneeY: 106, bandR: 104, viewBoxX: 5, viewBoxY: -15, viewBoxW: 230, viewBoxH: 240, imageOffsetX: -10, imageOffsetY: -14 } },
  // Heel Slide renders bands as a HORIZONTAL bar below the body image
  // (LinearBandBar) instead of the curved arc dial. The `bandLayout` flag
  // inside `dial` is read by ImageSwapFigure to swap renderers. Other
  // dial coords stay so the linear bar can still position itself relative
  // to the figure container.
  //
  // min:0 — negative angles (the -10..0 visual tolerance buffer below
  // rest) render the 0° "leg flat" frame instead of the minus5 frame.
  // For heel slide, anything below rest looks identical to rest from
  // the patient's POV; the bucket PNG would otherwise show a slightly
  // hyper-extended pose that doesn't match what the patient is doing.
  HEEL_SLIDE:     { folder: 'HeelSlide',    prefix: 'HeelSlide_',    convention: 'bucket',  step: 5, min:  0,  max: 150, dial: { kneeX: 166, kneeY: 130, bandR:  58, bandLayout: 'linear' } },
  // Progress Test asset. Dial coords tuned via the in-page tuner so the
  // semi-circle pivots on the HIP (not the knee). Arrow position is
  // computed from the standard polygon formula (same as every other
  // exercise) — no tickOffset overrides.
  // Geometry kept in lockstep with the patient app's HEEL_SLIDE_ROM anchor
  // (physio_app_web/business/exercise/exerciseAvatar.js) so the admin
  // Progress-Test goals preview aligns exactly with the patient dial.
  HEEL_SLIDE_ROM: { folder: 'HeelSlideROM', prefix: 'HeelSlideROM',  convention: 'integer', step: 5, min:  0,  max: 130, dial: { kneeX: 135, kneeY: 123, bandR: 120, angleOffset: 43, imageOffsetX: 4, imageOffsetY: -5, viewBoxX: 0, viewBoxY: 15, viewBoxW: 240, viewBoxH: 230, restAngle: 45 } },
  KNEE_TO_CHEST:  { folder: 'KneeToChest',  prefix: 'KneeToChest_',  convention: 'bucket',  step: 5, min: -5,  max: 135, dial: { kneeX: 166, kneeY: 130, bandR:  104, viewBoxX: 65, viewBoxY: -15, viewBoxW: 230, imageOffsetX: -5, imageOffsetY: 5 } },
  // Bucket PNGs ship -5..90 (no frames past 86-90). max:90 clamps deeper
  // flex angles to the 86-90 frame so urlFor() never points at a missing
  // file. Rep gates + sensor limits stay at the master config — the clamp
  // is purely visual. Dial coords copied from KNEE_TO_CHEST (same supine
  // geometry); fine-tune kneeX/Y in the Configure modal preview if needed.
  LYING_KNEE_FLEXION: { folder: 'LyingKneeFlexion', prefix: 'LyingKneeFlexion_', convention: 'bucket', step: 5, min: -5, max:  90, dial: { kneeX: 166, kneeY: 130, bandR: 80, viewBoxX: 30, viewBoxY: -15, viewBoxW: 230, imageOffsetX: -5, imageOffsetY: 5 } },
  KNEE_FLEXION:   { folder: 'KneeFlexion',  prefix: 'KneeFlexion_',  convention: 'bucket',  step: 5, min: -5,  max:  90, dial: { kneeX: 138, kneeY: 134, bandR:  80 } },
  // Short Arc Quad — bucket frames -5..75. dial is a FIRST PASS (knee pivot
  // estimated from the PNG); verify + fine-tune in the Configure modal preview.
  SHORT_ARC_QUAD: { folder: 'ShortArcQuad', prefix: 'ShortArcQuad_', convention: 'bucket',  step: 5, min: -5,  max:  75, dial: { kneeX:  180, kneeY: 110, bandR: 80, viewBoxX: 65, viewBoxY: -15, viewBoxW: 200, imageOffsetX: -10, imageOffsetY: 5, angleOffset: -33 } },
}

// ── Seated Heel Slide artwork variants ──────────────────────────────────────
// Two matched ROM frame sets ship on the server. Both are derived from
// HEEL_SLIDE_ROM by spread so the filename convention and dial geometry can
// never drift between them — only the folder differs:
//   HEEL_SLIDE_ROM     → HeelSlideROM/     refreshed art. The Progress Test
//                        goals previews (progress.goals.modal.js,
//                        progress.test.goals.settings.form.js) pin this kind
//                        by hand, so they are unaffected by the split.
//   HEEL_SLIDE_ROM_V1  → HeelSlideROM_v1/  the older art that shipped before
//                        the refresh. The original Seated Heel Slide
//                        (exerciseId 7) maps here — see exerciseAssetMap.js.
//   HEEL_SLIDE_ROM_2   → HeelSlideROM/     refreshed art, used by Seated Heel
//                        Slide 2 (exerciseId 9) via an explicit assetKind.
// This is the admin's Configure-modal PREVIEW geometry; the patient app carries
// the per-variant figure zoom/offset tuning for the live session (see
// physio_app_web/business/exercise/exerciseAvatar.js).
KIND.HEEL_SLIDE_ROM_V1 = { ...KIND.HEEL_SLIDE_ROM, folder: 'HeelSlideROM_v1' }
KIND.HEEL_SLIDE_ROM_2 = { ...KIND.HEEL_SLIDE_ROM }

// ── Short Arc Quad variants ─────────────────────────────────────────────────
// Two separate frame sets ship on the server, derived from one another by
// spread so the filename convention (ShortArcQuad_angle_*.png) and the -5..75
// bucket range can never drift — only the folder differs:
//   SHORT_ARC_QUAD    → ShortArcQuad/   the original Short Arc Quad (id 6),
//                       which stores no assetKind and resolves by name.
//   SHORT_ARC_QUAD_2  → ShortArcQuad2/  new art, used by Short Arc Quad 2
//                       (exerciseId 11) via an explicit assetKind.
// Unlike the heel-slide pair this is a new folder rather than an old-vs-
// refreshed split, so no exercise had to be migrated off shared art.
// This is the admin's Configure-modal PREVIEW geometry; the patient app carries
// the per-variant figure zoom/offset tuning for the live session (see
// physio_app_web/business/exercise/exerciseAvatar.js).
KIND.SHORT_ARC_QUAD_2 = { ...KIND.SHORT_ARC_QUAD, folder: 'ShortArcQuad2' }

// ── Sit to Stand variants ───────────────────────────────────────────────────
// Two separate frame sets ship on the server, derived from one another by
// spread so the filename convention (SitToStand_angle_*.png) and the 0..90
// clamp can never drift — only the folder differs:
//   SIT_TO_STAND    → SitToStand/   the original Sit to Stand (id 3), which
//                     stores no assetKind and resolves by name.
//   SIT_TO_STAND_2  → SitToStand2/  new art, used by Sit to Stand 2 via an
//                     explicit assetKind.
// Like the Short Arc Quad pair this is a new folder rather than an old-vs-
// refreshed split, so no exercise had to be migrated off shared art.
// This is the admin's Configure-modal PREVIEW geometry; the patient app carries
// the per-variant figure zoom/offset tuning for the live session (see
// physio_app_web/business/exercise/exerciseAvatar.js).
KIND.SIT_TO_STAND_2 = { ...KIND.SIT_TO_STAND, folder: 'SitToStand2' }

// ── Lying Knee Flexion variants ─────────────────────────────────────────────
// Two separate frame sets ship on the server, derived from one another by
// spread so the filename convention (LyingKneeFlexion_angle_*.png) and the
// -5..90 clamp can never drift — only the folder differs:
//   LYING_KNEE_FLEXION    → LyingKneeFlexion/   the original (id 5), which
//                           stores no assetKind and resolves by name.
//   LYING_KNEE_FLEXION_2  → LyingKneeFlexion2/  new art, used by Lying Knee
//                           Flexion 2 via an explicit assetKind.
// Like the Short Arc Quad and Sit to Stand pairs this is a new folder rather
// than an old-vs-refreshed split, so no exercise had to be migrated off shared
// art.
// This is the admin's Configure-modal PREVIEW geometry; the patient app carries
// the per-variant figure zoom/offset tuning for the live session (see
// physio_app_web/business/exercise/exerciseAvatar.js).
KIND.LYING_KNEE_FLEXION_2 = { ...KIND.LYING_KNEE_FLEXION, folder: 'LyingKneeFlexion2' }

// ── Heel Slide variants ─────────────────────────────────────────────────────
// Two separate frame sets ship on the server, derived from one another by
// spread so the filename convention (HeelSlide_angle_*.png) and the 0..150
// bucket range can never drift — only the folder differs:
//   HEEL_SLIDE    → HeelSlide/   the original supine Heel Slide (id 2), which
//                   stores no assetKind and resolves by name.
//   HEEL_SLIDE_2  → HeelSlide2/  new art, used by Heel Slide 2 via an explicit
//                   assetKind.
// Like the Short Arc Quad, Sit to Stand and Lying Knee Flexion pairs — and
// unlike the SEATED heel-slide pair — this is a new folder rather than an
// old-vs-refreshed split, so no exercise had to be migrated off shared art.
// Nothing needs re-anchoring on the knee either: this family renders its bands
// as a horizontal bar below the body (bandLayout: 'linear'), so the dial coords
// are not pinned to the artwork.
// This is the admin's Configure-modal PREVIEW geometry; the patient app carries
// the per-variant figure tuning for the live session (see
// physio_app_web/business/exercise/exerciseAvatar.js).
KIND.HEEL_SLIDE_2 = { ...KIND.HEEL_SLIDE, folder: 'HeelSlide2' }

// ── Knee to Chest variants ──────────────────────────────────────────────────
// Two separate frame sets ship on the server, derived from one another by
// spread so the filename convention (KneeToChest_angle_*.png) and the -5..135
// clamp can never drift — only the folder differs:
//   KNEE_TO_CHEST    → KneeToChest/   the original Knee to Chest (id 4), which
//                      stores no assetKind and resolves by name.
//   KNEE_TO_CHEST_2  → KneeToChest2/  new art, used by Knee to Chest 2 via an
//                      explicit assetKind.
// Like the Short Arc Quad, Sit to Stand and Lying Knee Flexion pairs this is a
// new folder rather than an old-vs-refreshed split, so no exercise had to be
// migrated off shared art.
// This is the admin's Configure-modal PREVIEW geometry; the patient app carries
// the per-variant figure zoom/offset tuning for the live session (see
// physio_app_web/business/exercise/exerciseAvatar.js).
KIND.KNEE_TO_CHEST_2 = { ...KIND.KNEE_TO_CHEST, folder: 'KneeToChest2' }

// ── Mini Squat ──────────────────────────────────────────────────────────────
// A new exercise (not a re-skin), on portrait 900x1024 frames 0..90 with no
// `_minus5`. The admin preview renders in the same 200/210 container as the
// patient app and honours imageScale, so this carries the patient app's dial
// and body transform unchanged — unlike the `…_2` kinds above, the preview is
// the session geometry. See MINI_SQUAT in
// physio_app_web/business/exercise/exerciseAvatar.js for how it was placed and
// why it is `mirrored`. Keep the two in lockstep when it is retuned.
KIND.MINI_SQUAT = { folder: 'MiniSquat', prefix: 'MiniSquat_', convention: 'bucket', step: 5, min: 0, max: 90, dial: { kneeX: 80, kneeY: 115, bandR: 112, angleOffset: -90, mirrored: true, imageOffsetX: -3, imageOffsetY: -18, imageScale: 0.8, aspectRatio: '200 / 212' } }

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

function bucketSuffix(angle) {
  const a = Math.round(angle)
  if (a < 0)  return 'minus5'
  if (a === 0) return '0'
  const upper = Math.ceil(a / 5) * 5
  const lower = upper - 4
  return `${lower}-${upper}`
}

function integerSuffix(angle, step) {
  const snapped = Math.round(angle / step) * step
  return String(snapped)
}

export function listKinds() { return Object.keys(KIND) }
export function configFor(kind) { return KIND[kind] }

// Per-kind RomBandDial anchor (see KIND map above for tuning notes).
// Returns null for unknown kinds so callers can fall back to dial defaults.
export function dialAnchorFor(kind) {
  return (KIND[kind] && KIND[kind].dial) || null
}

export function urlFor(kind, angle) {
  const cfg = KIND[kind]
  if (!cfg) return null
  const clamped = clamp(angle, cfg.min, cfg.max)
  const fileBase =
    cfg.convention === 'integer'
      ? `${cfg.prefix}-${integerSuffix(clamped, cfg.step)}`
      : `${cfg.prefix}angle_${bucketSuffix(clamped)}`
  return `${SERVER_BASE}/content/exercises/${cfg.folder}/${fileBase}.png`
}

export function listUrls(kind) {
  const cfg = KIND[kind]
  if (!cfg) return []
  const seen = new Set()
  const urls = []
  for (let a = cfg.min; a <= cfg.max; a++) {
    const url = urlFor(kind, a)
    if (url && !seen.has(url)) {
      seen.add(url)
      urls.push(url)
    }
  }
  return urls
}

// Walk outward from `angle` until we find a URL that's actually in `available`.
// Used when the computed bucket has no PNG on disk (e.g. LyingKicks has no
// LyingKicks_angle_minus5). Returns null if `available` is empty.
export function nearestAvailableUrl(kind, angle, available) {
  if (!available || available.size === 0) return null
  const cfg = KIND[kind]
  if (!cfg) return null
  const start = Math.round(angle)
  const span = (cfg.max - cfg.min) + 5
  for (let d = 0; d <= span; d++) {
    const offsets = d === 0 ? [0] : [d, -d]
    for (const off of offsets) {
      const probe = start + off
      if (probe < cfg.min || probe > cfg.max) continue
      const url = urlFor(kind, probe)
      if (url && available.has(url)) return url
    }
  }
  return null
}
